import React, { useState, useRef, useCallback } from 'react';
import api from '../api';

// ── Styles ──────────────────────────────────────────────
const C = {
    bg: '#0a0a0f', card: '#12121a', hover: '#1a1a26', active: '#222233',
    border: '#2a2a3d', borderLight: '#3a3a55',
    text: '#e8e8f0', muted: '#8888a8', dim: '#5a5a78',
    accent: '#6366f1', accentHover: '#818cf8', accentDim: 'rgba(99,102,241,0.15)',
    success: '#22c55e', warning: '#f59e0b', danger: '#ef4444',
};

const BLOCK_TYPES = [
    { type: 'text', label: 'Text Block', icon: '¶' },
    { type: 'image', label: 'Image', icon: '🖼' },
    { type: 'button', label: 'Button', icon: '▢' },
    { type: 'divider', label: 'Divider', icon: '—' },
    { type: 'spacer', label: 'Spacer', icon: '↕' },
    { type: 'columns', label: '2 Columns', icon: '⊞' },
    { type: 'header', label: 'Header', icon: 'H' },
    { type: 'social', label: 'Social Links', icon: '★' },
];

function createBlock(type) {
    const id = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    const defaults = {
        text: { id, type, content: '<p>Click to edit this text. Use <b>bold</b>, <i>italic</i>, and merge fields like {{first_name}}.</p>', padding: '16px 24px' },
        image: { id, type, src: '', alt: 'Image', width: '100%', align: 'center', href: '', padding: '8px 24px' },
        button: { id, type, text: 'Click Here', href: 'https://', bgColor: '#6366f1', textColor: '#ffffff', borderRadius: '6px', align: 'center', padding: '16px 24px' },
        divider: { id, type, color: '#2a2a3d', thickness: '1px', width: '100%', padding: '8px 24px' },
        spacer: { id, type, height: '24px' },
        columns: { id, type, left: '<p>Left column content</p>', right: '<p>Right column content</p>', padding: '16px 24px' },
        header: { id, type, content: '<h1 style="margin:0;font-size:28px;font-weight:700;">Email Header</h1>', padding: '24px', bgColor: '#12121a' },
        social: { id, type, links: [
            { platform: 'Website', url: 'https://' },
            { platform: 'LinkedIn', url: 'https://linkedin.com/' },
        ], align: 'center', padding: '16px 24px' },
    };
    return defaults[type] || defaults.text;
}

// ── Inline Rich Text Editor ────────────────────────────
function RichTextEditor({ value, onChange, placeholder }) {
    const ref = useRef(null);
    const [showToolbar, setShowToolbar] = useState(false);

    const exec = (cmd, val) => {
        document.execCommand(cmd, false, val);
        if (ref.current) onChange(ref.current.innerHTML);
    };

    const ToolBtn = ({ cmd, val, label, title }) => (
        <button title={title || label} onClick={(e) => { e.preventDefault(); exec(cmd, val); }}
            style={{ background: 'none', border: '1px solid ' + C.border, color: C.text, padding: '2px 8px',
                borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
            {label}
        </button>
    );

    return (
        <div style={{ position: 'relative' }}>
            {showToolbar && (
                <div style={{ display: 'flex', gap: 4, padding: '6px 8px', background: C.card,
                    border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <ToolBtn cmd="bold" label="B" title="Bold" />
                    <ToolBtn cmd="italic" label="I" title="Italic" />
                    <ToolBtn cmd="underline" label="U" title="Underline" />
                    <ToolBtn cmd="strikethrough" label="S" title="Strikethrough" />
                    <span style={{ width: 1, background: C.border, margin: '0 4px' }} />
                    <ToolBtn cmd="formatBlock" val="h1" label="H1" />
                    <ToolBtn cmd="formatBlock" val="h2" label="H2" />
                    <ToolBtn cmd="formatBlock" val="p" label="P" />
                    <span style={{ width: 1, background: C.border, margin: '0 4px' }} />
                    <ToolBtn cmd="insertUnorderedList" label="•" title="Bullet list" />
                    <ToolBtn cmd="insertOrderedList" label="1." title="Numbered list" />
                    <span style={{ width: 1, background: C.border, margin: '0 4px' }} />
                    <ToolBtn cmd="justifyLeft" label="←" title="Align left" />
                    <ToolBtn cmd="justifyCenter" label="↔" title="Center" />
                    <ToolBtn cmd="justifyRight" label="→" title="Align right" />
                    <span style={{ width: 1, background: C.border, margin: '0 4px' }} />
                    <button onClick={(e) => {
                        e.preventDefault();
                        const url = prompt('Enter link URL:');
                        if (url) exec('createLink', url);
                    }} style={{ background: 'none', border: `1px solid ${C.border}`, color: C.accent,
                        padding: '2px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
                        🔗 Link
                    </button>
                    <button onClick={(e) => {
                        e.preventDefault();
                        const color = prompt('Enter hex color (e.g. #ff0000):');
                        if (color) exec('foreColor', color);
                    }} style={{ background: 'none', border: `1px solid ${C.border}`, color: C.warning,
                        padding: '2px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
                        🎨 Color
                    </button>
                </div>
            )}
            <div ref={ref} contentEditable suppressContentEditableWarning
                onFocus={() => setShowToolbar(true)}
                onBlur={() => { setTimeout(() => setShowToolbar(false), 200); if (ref.current) onChange(ref.current.innerHTML); }}
                onInput={() => { if (ref.current) onChange(ref.current.innerHTML); }}
                dangerouslySetInnerHTML={{ __html: value }}
                style={{
                    minHeight: 60, padding: '8px 12px', outline: 'none',
                    color: C.text, fontSize: 14, lineHeight: 1.6,
                    border: `1px solid transparent`, borderRadius: 6,
                    transition: 'border-color 0.2s',
                }}
                data-placeholder={placeholder}
            />
        </div>
    );
}

// ── Block Renderers ────────────────────────────────────
function TextBlockEditor({ block, onChange }) {
    return <RichTextEditor value={block.content} onChange={(v) => onChange({ ...block, content: v })} />;
}

function ImageBlockEditor({ block, onChange }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const result = await api.uploadImage(file);
            onChange({ ...block, src: result.url });
        } catch (err) {
            alert('Upload failed: ' + err.message);
        }
        setUploading(false);
    };

    return (
        <div style={{ textAlign: block.align || 'center', padding: block.padding }}>
            {block.src ? (
                <div>
                    <img src={block.src} alt={block.alt} style={{ maxWidth: block.width || '100%', borderRadius: 4 }} />
                    <div style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'center' }}>
                        <input value={block.src} onChange={(e) => onChange({ ...block, src: e.target.value })}
                            style={{ flex: 1, padding: '4px 8px', background: C.hover, border: `1px solid ${C.border}`,
                                borderRadius: 4, color: C.text, fontSize: 11, fontFamily: 'inherit' }}
                            placeholder="Image URL" />
                        <input value={block.href || ''} onChange={(e) => onChange({ ...block, href: e.target.value })}
                            style={{ width: 200, padding: '4px 8px', background: C.hover, border: `1px solid ${C.border}`,
                                borderRadius: 4, color: C.text, fontSize: 11, fontFamily: 'inherit' }}
                            placeholder="Link URL (optional)" />
                        <button onClick={() => fileRef.current?.click()}
                            style={{ padding: '4px 12px', background: C.accentDim, border: `1px solid ${C.accent}`,
                                borderRadius: 4, color: C.accent, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' }}>
                            Replace
                        </button>
                    </div>
                </div>
            ) : (
                <div onClick={() => fileRef.current?.click()}
                    style={{ padding: 40, border: `2px dashed ${C.border}`, borderRadius: 8, cursor: 'pointer',
                        color: C.muted, textAlign: 'center' }}>
                    {uploading ? 'Uploading...' : (
                        <>
                            <div style={{ fontSize: 32, marginBottom: 8 }}>🖼</div>
                            <div style={{ fontSize: 13, marginBottom: 4 }}>Click to upload image</div>
                            <div style={{ fontSize: 11 }}>or paste a URL below</div>
                            <input value={block.src} onChange={(e) => onChange({ ...block, src: e.target.value })}
                                onClick={(e) => e.stopPropagation()}
                                style={{ marginTop: 12, width: '80%', padding: '6px 10px', background: C.hover,
                                    border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontSize: 12,
                                    textAlign: 'center', fontFamily: 'inherit' }}
                                placeholder="https://example.com/image.jpg" />
                        </>
                    )}
                </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} style={{ display: 'none' }} />
        </div>
    );
}

function ButtonBlockEditor({ block, onChange }) {
    return (
        <div style={{ textAlign: block.align || 'center', padding: block.padding }}>
            <div style={{ display: 'inline-block', padding: '12px 32px', background: block.bgColor || '#6366f1',
                color: block.textColor || '#fff', borderRadius: block.borderRadius || '6px',
                fontWeight: 600, fontSize: 15, cursor: 'default' }}>
                {block.text}
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                <input value={block.text} onChange={(e) => onChange({ ...block, text: e.target.value })}
                    style={{ padding: '4px 8px', background: C.hover, border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontSize: 11, fontFamily: 'inherit' }}
                    placeholder="Button text" />
                <input value={block.href} onChange={(e) => onChange({ ...block, href: e.target.value })}
                    style={{ width: 200, padding: '4px 8px', background: C.hover, border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontSize: 11, fontFamily: 'inherit' }}
                    placeholder="Link URL" />
                <input type="color" value={block.bgColor || '#6366f1'} onChange={(e) => onChange({ ...block, bgColor: e.target.value })}
                    style={{ width: 30, height: 26, border: 'none', cursor: 'pointer', background: 'none' }} title="Button color" />
                <select value={block.align || 'center'} onChange={(e) => onChange({ ...block, align: e.target.value })}
                    style={{ padding: '4px 8px', background: C.hover, border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontSize: 11, fontFamily: 'inherit' }}>
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                </select>
            </div>
        </div>
    );
}

function DividerBlockEditor({ block, onChange }) {
    return (
        <div style={{ padding: block.padding }}>
            <hr style={{ border: 'none', borderTop: `${block.thickness} solid ${block.color}`, width: block.width }} />
            <div style={{ display: 'flex', gap: 6, marginTop: 6, justifyContent: 'center' }}>
                <input type="color" value={block.color || '#2a2a3d'} onChange={(e) => onChange({ ...block, color: e.target.value })}
                    style={{ width: 30, height: 22, border: 'none', cursor: 'pointer' }} title="Divider color" />
                <select value={block.thickness} onChange={(e) => onChange({ ...block, thickness: e.target.value })}
                    style={{ padding: '2px 6px', background: C.hover, border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontSize: 11, fontFamily: 'inherit' }}>
                    <option value="1px">Thin</option><option value="2px">Medium</option><option value="4px">Thick</option>
                </select>
            </div>
        </div>
    );
}

function ColumnsBlockEditor({ block, onChange }) {
    return (
        <div style={{ display: 'flex', gap: 16, padding: block.padding }}>
            <div style={{ flex: 1, border: `1px dashed ${C.border}`, borderRadius: 6, padding: 4 }}>
                <div style={{ fontSize: 9, color: C.dim, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Left Column</div>
                <RichTextEditor value={block.left} onChange={(v) => onChange({ ...block, left: v })} />
            </div>
            <div style={{ flex: 1, border: `1px dashed ${C.border}`, borderRadius: 6, padding: 4 }}>
                <div style={{ fontSize: 9, color: C.dim, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Right Column</div>
                <RichTextEditor value={block.right} onChange={(v) => onChange({ ...block, right: v })} />
            </div>
        </div>
    );
}

// ── Block-to-HTML Converter ────────────────────────────
function blocksToHtml(blocks, settings = {}) {
    const bgColor = settings.bgColor || '#0a0a0f';
    const contentBg = settings.contentBg || '#12121a';
    const contentWidth = settings.contentWidth || '600px';

    const renderBlock = (block) => {
        switch (block.type) {
            case 'text':
            case 'header':
                return `<tr><td style="padding:${block.padding || '16px 24px'};${block.bgColor ? `background:${block.bgColor};` : ''}">${block.content}</td></tr>`;
            case 'image':
                const img = `<img src="${block.src}" alt="${block.alt || ''}" style="max-width:${block.width || '100%'};height:auto;display:block;${block.align === 'center' ? 'margin:0 auto;' : ''}" />`;
                const wrapped = block.href ? `<a href="${block.href}" target="_blank">${img}</a>` : img;
                return `<tr><td style="padding:${block.padding || '8px 24px'};text-align:${block.align || 'center'};">${wrapped}</td></tr>`;
            case 'button':
                return `<tr><td style="padding:${block.padding || '16px 24px'};text-align:${block.align || 'center'};"><a href="${block.href}" target="_blank" style="display:inline-block;padding:12px 32px;background:${block.bgColor || '#6366f1'};color:${block.textColor || '#fff'};text-decoration:none;border-radius:${block.borderRadius || '6px'};font-weight:600;font-size:15px;font-family:system-ui,sans-serif;">${block.text}</a></td></tr>`;
            case 'divider':
                return `<tr><td style="padding:${block.padding || '8px 24px'};"><hr style="border:none;border-top:${block.thickness} solid ${block.color};width:${block.width};margin:0;" /></td></tr>`;
            case 'spacer':
                return `<tr><td style="height:${block.height};line-height:${block.height};font-size:1px;">&nbsp;</td></tr>`;
            case 'columns':
                return `<tr><td style="padding:${block.padding || '16px 24px'};"><table width="100%" cellpadding="0" cellspacing="0"><tr><td width="48%" valign="top" style="padding-right:8px;">${block.left}</td><td width="4%"></td><td width="48%" valign="top" style="padding-left:8px;">${block.right}</td></tr></table></td></tr>`;
            case 'social':
                const links = (block.links || []).map(l =>
                    `<a href="${l.url}" target="_blank" style="display:inline-block;margin:0 8px;color:${C.accent};text-decoration:none;font-size:13px;">${l.platform}</a>`
                ).join('');
                return `<tr><td style="padding:${block.padding || '16px 24px'};text-align:${block.align || 'center'};">${links}</td></tr>`;
            default:
                return '';
        }
    };

    return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0">
<style>body{margin:0;padding:0;background:${bgColor};} a{color:${C.accent};} img{border:0;max-width:100%;}</style></head>
<body style="margin:0;padding:0;background:${bgColor};font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;color:${C.text};">
<table width="100%" cellpadding="0" cellspacing="0" style="background:${bgColor};">
<tr><td align="center" style="padding:24px 0;">
<table width="${contentWidth}" cellpadding="0" cellspacing="0" style="background:${contentBg};border-radius:8px;overflow:hidden;max-width:100%;">
${blocks.map(renderBlock).join('\n')}
</table>
</td></tr></table>
</body></html>`;
}

// ── Main Email Designer Component ──────────────────────
export default function EmailDesigner({ blocks, onChange, onHtmlChange }) {
    const [selectedBlock, setSelectedBlock] = useState(null);
    const [viewMode, setViewMode] = useState('edit'); // edit, preview, html
    const [dragOver, setDragOver] = useState(null);
    const [emailSettings, setEmailSettings] = useState({
        bgColor: '#0a0a0f', contentBg: '#1a1a26', contentWidth: '600px',
    });

    const addBlock = (type) => {
        const newBlock = createBlock(type);
        const updated = [...blocks, newBlock];
        onChange(updated);
        if (onHtmlChange) onHtmlChange(blocksToHtml(updated, emailSettings));
        setSelectedBlock(newBlock.id);
    };

    const updateBlock = (block) => {
        const updated = blocks.map(b => b.id === block.id ? block : b);
        onChange(updated);
        if (onHtmlChange) onHtmlChange(blocksToHtml(updated, emailSettings));
    };

    const removeBlock = (id) => {
        const updated = blocks.filter(b => b.id !== id);
        onChange(updated);
        if (onHtmlChange) onHtmlChange(blocksToHtml(updated, emailSettings));
        if (selectedBlock === id) setSelectedBlock(null);
    };

    const moveBlock = (id, direction) => {
        const idx = blocks.findIndex(b => b.id === id);
        if ((direction === -1 && idx === 0) || (direction === 1 && idx === blocks.length - 1)) return;
        const updated = [...blocks];
        [updated[idx], updated[idx + direction]] = [updated[idx + direction], updated[idx]];
        onChange(updated);
        if (onHtmlChange) onHtmlChange(blocksToHtml(updated, emailSettings));
    };

    const duplicateBlock = (id) => {
        const idx = blocks.findIndex(b => b.id === id);
        const newBlock = { ...blocks[idx], id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6) };
        const updated = [...blocks.slice(0, idx + 1), newBlock, ...blocks.slice(idx + 1)];
        onChange(updated);
        if (onHtmlChange) onHtmlChange(blocksToHtml(updated, emailSettings));
    };

    const renderBlockEditor = (block) => {
        switch (block.type) {
            case 'text': return <TextBlockEditor block={block} onChange={updateBlock} />;
            case 'header': return <TextBlockEditor block={block} onChange={updateBlock} />;
            case 'image': return <ImageBlockEditor block={block} onChange={updateBlock} />;
            case 'button': return <ButtonBlockEditor block={block} onChange={updateBlock} />;
            case 'divider': return <DividerBlockEditor block={block} onChange={updateBlock} />;
            case 'spacer': return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
                    <span style={{ fontSize: 11, color: C.dim }}>Spacer: </span>
                    <select value={block.height} onChange={(e) => updateBlock({ ...block, height: e.target.value })}
                        style={{ marginLeft: 4, padding: '2px 6px', background: C.hover, border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontSize: 11, fontFamily: 'inherit' }}>
                        <option value="8px">8px</option><option value="16px">16px</option><option value="24px">24px</option>
                        <option value="32px">32px</option><option value="48px">48px</option>
                    </select>
                </div>
            );
            case 'columns': return <ColumnsBlockEditor block={block} onChange={updateBlock} />;
            case 'social': return (
                <div style={{ textAlign: block.align, padding: block.padding }}>
                    {(block.links || []).map((l, i) => (
                        <div key={i} style={{ display: 'inline-flex', gap: 4, margin: '0 4px' }}>
                            <input value={l.platform} onChange={(e) => {
                                const links = [...block.links]; links[i] = { ...l, platform: e.target.value };
                                updateBlock({ ...block, links });
                            }} style={{ width: 80, padding: '3px 6px', background: C.hover, border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontSize: 11, fontFamily: 'inherit' }} />
                            <input value={l.url} onChange={(e) => {
                                const links = [...block.links]; links[i] = { ...l, url: e.target.value };
                                updateBlock({ ...block, links });
                            }} style={{ width: 150, padding: '3px 6px', background: C.hover, border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontSize: 11, fontFamily: 'inherit' }} />
                        </div>
                    ))}
                    <button onClick={() => updateBlock({ ...block, links: [...(block.links || []), { platform: 'New', url: 'https://' }] })}
                        style={{ display: 'block', margin: '8px auto 0', padding: '3px 10px', background: C.hover, border: `1px solid ${C.border}`, borderRadius: 4, color: C.muted, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' }}>
                        + Add link
                    </button>
                </div>
            );
            default: return null;
        }
    };

    const html = blocksToHtml(blocks, emailSettings);

    return (
        <div className="email-designer" style={{ display: 'flex', height: '100%', minHeight: 500, gap: 0 }}>
            {/* Left: Block Palette */}
            <div className="designer-palette" style={{ width: 180, background: C.card, borderRight: `1px solid ${C.border}`, padding: 12, flexShrink: 0, overflowY: 'auto' }}>
                <div style={{ fontSize: 10, color: C.dim, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                    Add Blocks
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {BLOCK_TYPES.map(bt => (
                        <button key={bt.type} onClick={() => addBlock(bt.type)}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: C.hover,
                                border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, cursor: 'pointer',
                                fontSize: 12, textAlign: 'left', fontFamily: 'inherit', transition: 'border-color 0.15s' }}
                            onMouseOver={(e) => e.currentTarget.style.borderColor = C.accent}
                            onMouseOut={(e) => e.currentTarget.style.borderColor = C.border}>
                            <span style={{ fontSize: 16, width: 24, textAlign: 'center' }}>{bt.icon}</span>
                            {bt.label}
                        </button>
                    ))}
                </div>

                <div style={{ fontSize: 10, color: C.dim, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '20px 0 10px' }}>
                    Merge Fields
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {['{{first_name}}', '{{last_name}}', '{{company}}', '{{title}}', '{{sender_name}}', '{{unsubscribe_url}}'].map(f => (
                        <code key={f} onClick={() => { navigator.clipboard.writeText(f); }}
                            style={{ fontSize: 10, padding: '4px 8px', borderRadius: 4, background: C.accentDim,
                                color: C.accent, cursor: 'pointer', display: 'block' }}
                            title="Click to copy">
                            {f}
                        </code>
                    ))}
                </div>

                <div style={{ fontSize: 10, color: C.dim, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '20px 0 10px' }}>
                    Email Settings
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: 11, color: C.muted }}>Background
                        <input type="color" value={emailSettings.bgColor} onChange={(e) => setEmailSettings(s => ({ ...s, bgColor: e.target.value }))}
                            style={{ display: 'block', width: '100%', height: 24, border: 'none', cursor: 'pointer', marginTop: 2 }} />
                    </label>
                    <label style={{ fontSize: 11, color: C.muted }}>Content BG
                        <input type="color" value={emailSettings.contentBg} onChange={(e) => setEmailSettings(s => ({ ...s, contentBg: e.target.value }))}
                            style={{ display: 'block', width: '100%', height: 24, border: 'none', cursor: 'pointer', marginTop: 2 }} />
                    </label>
                    <label style={{ fontSize: 11, color: C.muted }}>Width
                        <select value={emailSettings.contentWidth} onChange={(e) => setEmailSettings(s => ({ ...s, contentWidth: e.target.value }))}
                            style={{ display: 'block', width: '100%', padding: '3px 6px', background: C.hover, border: `1px solid ${C.border}`, borderRadius: 4, color: C.text, fontSize: 11, marginTop: 2, fontFamily: 'inherit' }}>
                            <option value="500px">500px (Narrow)</option>
                            <option value="600px">600px (Standard)</option>
                            <option value="700px">700px (Wide)</option>
                        </select>
                    </label>
                </div>
            </div>

            {/* Center: Canvas */}
            <div className="designer-canvas" style={{ flex: 1, overflowY: 'auto', background: '#0d0d14' }}>
                {/* View mode tabs */}
                <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${C.border}`, background: C.card, position: 'sticky', top: 0, zIndex: 10 }}>
                    {['edit', 'preview', 'html'].map(mode => (
                        <button key={mode} onClick={() => setViewMode(mode)}
                            style={{ padding: '10px 20px', background: viewMode === mode ? C.active : 'transparent',
                                border: 'none', borderBottom: viewMode === mode ? `2px solid ${C.accent}` : '2px solid transparent',
                                color: viewMode === mode ? C.text : C.muted, cursor: 'pointer', fontSize: 12,
                                fontWeight: 600, textTransform: 'capitalize', fontFamily: 'inherit' }}>
                            {mode === 'html' ? 'HTML Code' : mode}
                        </button>
                    ))}
                </div>

                {viewMode === 'edit' && (
                    <div style={{ maxWidth: 640, margin: '24px auto', padding: '0 16px' }}>
                        {blocks.length === 0 && (
                            <div style={{ textAlign: 'center', padding: 60, color: C.dim }}>
                                <div style={{ fontSize: 48, marginBottom: 12 }}>📧</div>
                                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Start designing your email</div>
                                <div style={{ fontSize: 13 }}>Click a block type on the left to add it here</div>
                            </div>
                        )}

                        {blocks.map((block, idx) => (
                            <div key={block.id}
                                onClick={() => setSelectedBlock(block.id)}
                                style={{
                                    position: 'relative', marginBottom: 4,
                                    border: `1px solid ${selectedBlock === block.id ? C.accent : 'transparent'}`,
                                    borderRadius: 6, background: C.card,
                                    transition: 'border-color 0.15s',
                                    cursor: 'default',
                                }}>
                                {/* Block controls */}
                                {selectedBlock === block.id && (
                                    <div style={{
                                        position: 'absolute', top: -12, right: 8, display: 'flex', gap: 2,
                                        background: C.card, border: `1px solid ${C.border}`, borderRadius: 6,
                                        padding: '2px 4px', zIndex: 5,
                                    }}>
                                        <button onClick={(e) => { e.stopPropagation(); moveBlock(block.id, -1); }}
                                            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', padding: '2px 6px', fontSize: 14 }} title="Move up">↑</button>
                                        <button onClick={(e) => { e.stopPropagation(); moveBlock(block.id, 1); }}
                                            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', padding: '2px 6px', fontSize: 14 }} title="Move down">↓</button>
                                        <button onClick={(e) => { e.stopPropagation(); duplicateBlock(block.id); }}
                                            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', padding: '2px 6px', fontSize: 12 }} title="Duplicate">⧉</button>
                                        <button onClick={(e) => { e.stopPropagation(); removeBlock(block.id); }}
                                            style={{ background: 'none', border: 'none', color: C.danger, cursor: 'pointer', padding: '2px 6px', fontSize: 14 }} title="Delete">✕</button>
                                    </div>
                                )}
                                {renderBlockEditor(block)}
                            </div>
                        ))}
                    </div>
                )}

                {viewMode === 'preview' && (
                    <div style={{ maxWidth: 640, margin: '24px auto', background: emailSettings.bgColor, borderRadius: 8, overflow: 'hidden' }}>
                        <iframe srcDoc={html} title="Email Preview"
                            style={{ width: '100%', minHeight: 600, border: 'none', borderRadius: 8 }} />
                    </div>
                )}

                {viewMode === 'html' && (
                    <div style={{ padding: 16 }}>
                        <textarea readOnly value={html}
                            style={{ width: '100%', minHeight: 500, padding: 16, background: C.card,
                                border: `1px solid ${C.border}`, borderRadius: 8, color: C.accent,
                                fontFamily: "'JetBrains Mono', monospace", fontSize: 12, lineHeight: 1.5,
                                resize: 'vertical' }} />
                        <button onClick={() => { navigator.clipboard.writeText(html); }}
                            style={{ marginTop: 8, padding: '8px 16px', background: C.accentDim, border: `1px solid ${C.accent}`,
                                borderRadius: 6, color: C.accent, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
                            Copy HTML
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export { blocksToHtml, createBlock };
